import { useNow, useStorage } from "@vueuse/core";
import { computed } from "vue";

interface UseSubmitLockOptions {
  /** Lock duration in milliseconds */
  durationMs: number;
  storage?: Storage;
}

/**
 * Persistent submit lock based on time.
 */
export function useSubmitLock(storageKey: string, options: UseSubmitLockOptions) {
  const { durationMs, storage } = options;

  const now = useNow();

  const lastSubmitted = useStorage<number | undefined>(storageKey, undefined, storage);

  const timeSinceLastSubmit = computed(() => {
    if (lastSubmitted.value === undefined) {
      return Infinity;
    }
    return now.value.getTime() - lastSubmitted.value;
  });

  const isLocked = computed(() => {
    return timeSinceLastSubmit.value < durationMs;
  });

  const timeRemainingMs = computed(() => {
    if (!isLocked.value) {
      return 0;
    }
    return Math.max(0, durationMs - timeSinceLastSubmit.value);
  });

  const lock = () => {
    lastSubmitted.value = now.value.getTime();
  };

  /** Manually clear the lock */
  const reset = () => {
    lastSubmitted.value = undefined;
  };

  return {
    /** True if submissions should be blocked */
    isLocked,

    /** Remaining lock time in milliseconds */
    timeRemainingMs,

    /** start lock */
    lock,

    /** Clear lock manually */
    reset,
  };
}
